/**
 * Whether the adopt dialog's apply button can be pressed, and what it says.
 *
 * Pure - `web` has no test runner of its own; see adoptApplyState.test.ts.
 */

export interface AdoptApplyInput {
  /**
   * The server's `applyable`, from the plan: the one rule for whether the file
   * has anything to adopt. Existing documents never make it false (decision 7,
   * 2026-09-15).
   */
  planApplyable: boolean;
  /** Rows that would change a form: automatic, accepted proposals and additions, minus skipped. */
  selectedCount: number;
  /** Rows the forms already agree with. */
  unchangedCount: number;
  /**
   * Ticked section changes: reorders and repeating blocks (D2). On FOG they can
   * be the only thing that changes a form. Absent means none.
   */
  sectionChangeCount?: number;
}

export interface AdoptApplyView {
  enabled: boolean;
  /** True when adopting changes no form and only stores the dictionary. */
  storesOnly: boolean;
  label: string;
  title: string;
}

/**
 * A plan whose rows all already match the forms selects nothing, and must still
 * be adoptable: adopting stores the file as the study's dictionary even when no
 * form changes. Re-adopting FOG's v4 file (1,591 rows, all unchanged) is exactly
 * that case, and a selected-count rule alone left its button disabled.
 */
export function adoptApplyState(i: AdoptApplyInput): AdoptApplyView {
  const changes = i.selectedCount + (i.sectionChangeCount ?? 0);
  const storesOnly = changes === 0 && i.unchangedCount > 0;
  const enabled = i.planApplyable && (changes > 0 || storesOnly);
  if (enabled && storesOnly) {
    return {
      enabled, storesOnly,
      label: 'Adopt dictionary (no form changes)',
      title: 'Every row already matches the forms. The dictionary will be stored without changing any form.',
    };
  }
  return {
    enabled,
    storesOnly: false,
    label: `Adopt ${changes} change(s)`,
    title: enabled
      ? 'Make these forms match the dictionary'
      : 'Nothing is selected to adopt',
  };
}
